import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal } from '../components/Modal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Import company images
import netflippImg from '../assets/images/netflipp.webp';
import yesGroupImg from '../assets/images/yes-group-holding.webp';
import bssHoldingImg from '../assets/images/bss-group-holding.webp';
import marvelLogoImg from '../assets/images/Marvel Logo (1).webp';
import bssGroupImg from '../assets/images/bss-group-holding.webp';
import allSoftwareImg from '../assets/images/all-software-24.webp';
import worldBusinessImg from '../assets/images/world-business-ag.webp';
import gluckspilzImg from '../assets/images/gluckspilz-gastro.webp';
import tradeOnTvImg from '../assets/images/tradeontv.webp';

// Interface for company data
interface Company {
  id: number;
  name: string;
  category: string;
  categoryTr?: string;
  categoryFr?: string;
  categoryEs?: string;
  categoryAr?: string;
  image: string;
  description: string;
  descriptionTr?: string;
  descriptionFr?: string;
  descriptionEs?: string;
  descriptionAr?: string;
  link?: string;
}

// Companies page styled components
const CompaniesSection = styled.section`
  background: #ffffff;
  min-height: 100vh;
  padding: 80px 0;
  position: relative;
  overflow-x: hidden;
`;

const CompaniesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
  overflow-x: hidden;
`;

const CompaniesHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: #000;
  margin-bottom: 20px;
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: #000;
    margin: 15px auto 0;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: #333;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

// Carousel components
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 40px 0 60px;
  height: 520px;
  margin-bottom: 60px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 480px;
    padding: 20px 0 50px;
    margin-bottom: 50px;
    touch-action: pan-y;
  }
  
  @media (max-width: 480px) {
    height: 450px;
    padding: 15px 0 45px;
    margin-bottom: 40px;
  }
`;

const CarouselTrack = styled(motion.div)`
  display: flex;
  position: absolute;
  height: 100%;
  width: fit-content;
  pointer-events: auto;
  will-change: transform; /* Optimize for animations */
  touch-action: pan-x;
  transform: translateZ(0); /* Hardware acceleration */
  backface-visibility: hidden; /* Prevent visual glitches */
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  z-index: 2;
`;

const CarouselCard = styled(motion.div)`
  background: white;
  border-radius: 8px;
  width: 320px;
  height: 420px;
  margin: 0 20px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 280px;
    height: 380px;
    margin: 0 15px;
  }
  
  @media (max-width: 480px) {
    width: 260px;
    height: 360px;
    margin: 0 10px;
  }
`;

const CompanyImageWrapper = styled.div`
  height: 160px;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  overflow: hidden;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
`;

const WorldBusinessImageWrapper = styled(CompanyImageWrapper)`
  padding: 0;
`;

const CompanyImage = styled.img`
  max-width: 85%;
  max-height: 85%;
  object-fit: contain;
`;

const WorldBusinessCompanyImage = styled(CompanyImage)`
  max-width: 100%;
  max-height: 100%;
  padding: 0;
`;

const CompanyContent = styled.div`
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CompanyCategory = styled.span`
  font-size: 0.8rem;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const CompanyName = styled.h3`
  font-size: 1.25rem;
  color: #111;
  margin-bottom: 12px;
  font-weight: 500;
`;

const CompanyDescription = styled.p`
  font-size: 0.95rem;
  color: #555;
  flex-grow: 1;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const NavigationButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #ffffff;
  border: none;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  svg {
    width: 36px;
    height: 36px;
    transition: transform 0.2s ease;
  }

  &:hover:not(:disabled) {
    background-color: #ffffff;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    transform: translateY(-50%) scale(1.05);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    
    svg {
      width: 32px;
      height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 30px;
  
  @media (max-width: 480px) {
    left: 15px;
  }
`;

const NextButton = styled(NavigationButton)`
  right: 30px;
  
  @media (max-width: 480px) {
    right: 15px;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 15px 0;
  gap: 12px;
  
  @media (max-width: 768px) {
    bottom: -25px;
    gap: 10px;
  }
`;

// Şirket detay kartı için styled-components
const FeaturedCompanyCard = styled.div`
  display: flex;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin: 40px auto;
  max-width: 900px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 90%;
  }
`;

// Detay görüntüleme butonu için styled-component
const ViewDetailsButton = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: auto;
  align-self: flex-start;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &:hover {
    background: #333;
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
  }
`;

const Dot = styled.button<{ active: boolean }>`
  width: 12px;
  height: 12px;
  min-width: 12px;
  min-height: 12px;
  border-radius: 9999px;
  background: ${props => props.active ? '#000' : '#e0e0e0'};
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0, 0, 0, 0.3)' : 'none'};
  transform: ${props => props.active ? 'scale(1.2)' : 'scale(1)'};
  position: relative;
  z-index: 15;
  
  &:after {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border-radius: 50%;
  }
  
  &:hover {
    background: ${props => props.active ? '#000' : '#bbb'};
    transform: scale(1.2);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
    min-width: 10px;
    min-height: 10px;
  }
`;

const ActiveCompanyContainer = styled.div`
  margin-bottom: 80px;
  background: white;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActiveCompanyImageContainer = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.03);
`;

const ActiveCompanyImage = styled.img`
  max-width: 75%;
  max-height: 75%;
  object-fit: contain;
`;

const ActiveCompanyContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActiveCompanyCategory = styled.span`
  font-size: 0.9rem;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  font-weight: 500;
`;

const ActiveCompanyName = styled.h2`
  font-size: 2rem;
  color: #000;
  margin-bottom: 16px;
  font-weight: 500;
`;

const ActiveCompanyDescription = styled.p`
  font-size: 1.1rem;
  color: #333;
  line-height: 1.7;
  margin-bottom: 30px;
`;

const ActiveCompanyLink = styled(motion.a)`
  display: inline-block;
  background: #000;
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  align-self: flex-start;
  
  &:hover {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturedSection = styled.div`
  margin-top: 100px;
  background: #000;
  color: white;
  border-radius: 8px;
  padding: 60px 40px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
`;

const FeaturedTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: #fff;
  margin-bottom: 30px;
  font-weight: 300;
  letter-spacing: 0.05em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.5);
    bottom: -10px;
    left: 0;
  }
`;

const FeaturedContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedDescription = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  
  h3 {
    font-size: 1.5rem;
    color: white;
    margin-bottom: 20px;
    font-weight: 400;
  }
  
  p {
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.8);
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 25px;
    
    li {
      margin-bottom: 12px;
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const FeaturedImage = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 20px;
  
  img {
    max-width: 100%;
    height: auto;
  }
`;

const FeaturedLink = styled(motion.a)`
  display: inline-block;
  background: #fff;
  color: black;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  margin-top: 20px;
  transition: all 0.2s;
  
  &:hover {
    background: #eee;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

// Modal components
const ModalCompanyImage = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border-radius: 8px;
  margin-bottom: 20px;
  
  img {
    max-width: 75%;
    max-height: 75%;
    object-fit: contain;
  }
`;

// World Business AG özel stili
const WorldBusinessImage = styled(ModalCompanyImage)`
  img {
    max-width: 100%;
    max-height: 100%;
    padding: 0;
  }
`;

const ModalCompanyName = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #000;
  font-weight: 500;
`;

const ModalCompanyCategory = styled.div`
  font-size: 0.9rem;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  font-weight: 500;
`;

const ModalCompanyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 25px;
`;

const ModalCompanyLink = styled.a`
  display: inline-block;
  background: #000;
  color: white;
  padding: 10px 20px;
  margin-top: 20px;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const CompanyDetailsContainer = styled.div`
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const CompanyDetailsCategory = styled.span`
  font-size: 0.9rem;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  font-weight: 500;
  display: block;
`;

const CompanyDetailsName = styled.h2`
  font-size: 1.8rem;
  color: #000;
  margin: 10px 0;
  font-weight: 500;
`;

const CompanyDetailsSummary = styled.p`
  font-size: 1.1rem;
  color: #333;
  line-height: 1.7;
  margin-bottom: 20px;
`;

const CompanyDetailsLink = styled.a`
  display: inline-block;
  background: #000;
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ModalCompanyDetails = styled.div`
  margin-top: 25px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 25px;
`;

const ModalCompanyInfoItem = styled.div`
  margin-bottom: 10px;
  font-size: 0.95rem;
  color: #444;
  
  strong {
    font-weight: 600;
    margin-right: 5px;
  }
`;

// Add a styled component for swipe indicators
const SwipeIndicator = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.7);
  background-color: #ffffff;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 12px;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  animation: pulse 1.5s infinite;
  z-index: 10;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  
  @keyframes pulse {
    0% { opacity: 0.9; }
    50% { opacity: 1; }
    100% { opacity: 0.9; }
  }
  
  svg {
    width: 32px;
    height: 32px;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    display: flex;
    
    &.left {
      left: 20px;
    }
    
    &.right {
      right: 20px;
    }
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    
    svg {
      width: 28px;
      height: 28px;
    }
    
    &.left {
      left: 15px;
    }
    
    &.right {
      right: 15px;
    }
  }
`;

export default function Companies() {
  const { language } = useLanguage();
  
  // SEO metadata for Companies page
  const companiesSeoData = {
    en: {
      title: 'Barkin Sayin - Global Companies & Fintech Ventures',
      description: 'Explore companies founded and invested in by Barkin Sayin, including Trade on TV, All Software 24, and fintech ventures across Germany, UAE, and Turkey.',
      keywords: [
        'Barkin Sayin companies',
        'Trade on TV',
        'All Software 24',
        'YES Group',
        'BSS Holding',
        'Smart TV Investment platforms',
        'Fintech ventures Germany',
        'Global business portfolio'
      ]
    },
    tr: {
      title: 'Barkın Sayın - Global Şirketler & Fintech Girişimleri',
      description: 'Barkın Sayın tarafından kurulan ve yatırım yapılan Trade on TV, All Software 24 ve Almanya, BAE ve Türkiye\'deki fintech girişimlerini keşfedin.',
      keywords: [
        'Barkın Sayın şirketleri',
        'Trade on TV',
        'All Software 24',
        'YES Group',
        'BSS Holding',
        'Akıllı TV Yatırım platformları',
        'Almanya fintech girişimleri',
        'Global iş portfolyosu'
      ]
    }
  };
  
  const seoTitle = companiesSeoData[language as keyof typeof companiesSeoData]?.title || companiesSeoData.en.title;
  const seoDescription = companiesSeoData[language as keyof typeof companiesSeoData]?.description || companiesSeoData.en.description;
  const seoKeywords = companiesSeoData[language as keyof typeof companiesSeoData]?.keywords || companiesSeoData.en.keywords;
  
  // Add schema.org Organization markup for companies
  const companySchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Organization",
          "name": "Trade on TV",
          "description": "The first smart TV investment application",
          "url": "https://tradeontv.com"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Organization",
          "name": "All Software 24",
          "description": "Digital transformation and software development company"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Organization",
          "name": "YES Group",
          "description": "International business consulting and investment group"
        }
      }
    ]
  };
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [visibleCards, setVisibleCards] = useState(3);
  const [trackWidth, setTrackWidth] = useState(300);
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const swipeThreshold = 50; // Minimum swipe distance to trigger navigation
  const location = useLocation();
  const navigate = useNavigate();
  const companyDetailRef = useRef<HTMLDivElement>(null); // Şirket detay bölümü için referans

  // Define the companies data
  const companies = useMemo(() => [
    {
      id: 1,
      name: "AllSoftware24 L.L.C-FZ",
      category: "Software Development",
      categoryTr: "Yazılım Geliştirme",
      categoryFr: "Développement Logiciel",
      categoryEs: "Desarrollo de Software",
      categoryAr: "تطوير البرمجيات",
      image: allSoftwareImg,
      description: "Leading software development company specializing in customized solutions, web applications, mobile apps, and enterprise software systems with a focus on innovative technologies like AI and blockchain.",
      descriptionTr: "Özelleştirilmiş çözümler, web uygulamaları, mobil uygulamalar ve yapay zeka ve blockchain gibi yenilikçi teknolojilere odaklanan kurumsal yazılım sistemlerinde uzmanlaşmış lider yazılım geliştirme şirketi.",
      descriptionFr: "Société de développement logiciel de premier plan spécialisée dans les solutions personnalisées, les applications web, les applications mobiles et les systèmes logiciels d'entreprise, avec un accent sur les technologies innovantes comme l'IA et la blockchain.",
      descriptionEs: "Empresa líder en desarrollo de software especializada en soluciones personalizadas, aplicaciones web, aplicaciones móviles y sistemas de software empresarial con un enfoque en tecnologías innovadoras como IA y blockchain.",
      descriptionAr: "شركة رائدة في تطوير البرمجيات متخصصة في الحلول المخصصة وتطبيقات الويب وتطبيقات الجوال وأنظمة برمجيات المؤسسات مع التركيز على التقنيات المبتكرة مثل الذكاء الاصطناعي وبلوكتشين.",
      link: "https://allsoftware24.com/"
    },
    {
      id: 2,
      name: "Netflipp",
      category: "Digital Media",
      categoryTr: "Dijital Medya",
      categoryFr: "Médias Numériques",
      categoryEs: "Medios Digitales",
      categoryAr: "الوسائط الرقمية",
      image: netflippImg,
      description: "Innovative digital media platform providing streaming services and content distribution solutions for businesses and consumers.",
      descriptionTr: "İşletmeler ve tüketiciler için yayın hizmetleri ve içerik dağıtım çözümleri sunan yenilikçi dijital medya platformu.",
      descriptionFr: "Plateforme de médias numériques innovante fournissant des services de streaming et des solutions de distribution de contenu pour les entreprises et les consommateurs.",
      descriptionEs: "Plataforma innovadora de medios digitales que proporciona servicios de streaming y soluciones de distribución de contenido para empresas y consumidores.",
      descriptionAr: "منصة وسائط رقمية مبتكرة توفر خدمات البث وحلول توزيع المحتوى للشركات والمستهلكين.",
      link: "https://netflipp.de/"
    },
    {
      id: 3,
      name: "YES Group Holding",
      category: "Investment",
      categoryTr: "Yatırım",
      categoryFr: "Investissement",
      categoryEs: "Inversión",
      categoryAr: "استثمار",
      image: yesGroupImg,
      description: "Strategic investment group focused on emerging technologies and market opportunities across multiple industry sectors.",
      descriptionTr: "Birden fazla endüstri sektöründe gelişen teknolojilere ve pazar fırsatlarına odaklanan stratejik yatırım grubu.",
      descriptionFr: "Groupe d'investissement stratégique axé sur les technologies émergentes et les opportunités de marché dans plusieurs secteurs industriels.",
      descriptionEs: "Grupo de inversión estratégica centrado en tecnologías emergentes y oportunidades de mercado en múltiples sectores industriales.",
      descriptionAr: "مجموعة استثمارية استراتيجية تركز على التقنيات الناشئة وفرص السوق عبر قطاعات صناعية متعددة."
    },
    {
      id: 4,
      name: "BSS Holding",
      category: "Financial Services",
      categoryTr: "Finansal Hizmetler",
      categoryFr: "Services Financiers",
      categoryEs: "Servicios Financieros",
      categoryAr: "الخدمات المالية",
      image: bssHoldingImg,
      description: "Financial services conglomerate offering diverse solutions for institutional and retail clients in global markets.",
      descriptionTr: "Küresel piyasalarda kurumsal ve bireysel müşteriler için çeşitli çözümler sunan finansal hizmetler şirketi.",
      descriptionFr: "Conglomérat de services financiers offrant des solutions diverses pour les clients institutionnels et de détail sur les marchés mondiaux.",
      descriptionEs: "Conglomerado de servicios financieros que ofrece diversas soluciones para clientes institucionales y minoristas en mercados globales.",
      descriptionAr: "مجموعة خدمات مالية تقدم حلولاً متنوعة للعملاء المؤسسيين والأفراد في الأسواق العالمية.",
      link: "https://www.instagram.com/bssholding/"
    },
    {
      id: 5,
      name: "Marvel Immobilien",
      category: "Real Estate",
      categoryTr: "Gayrimenkul",
      categoryFr: "Immobilier",
      categoryEs: "Bienes Raíces",
      categoryAr: "عقارات",
      image: marvelLogoImg,
      description: "Premium real estate development and management firm specializing in commercial and residential properties.",
      descriptionTr: "Ticari ve konut gayrimenkulleri konusunda uzmanlaşmış premium gayrimenkul geliştirme ve yönetim firması.",
      descriptionFr: "Société de développement et de gestion immobilière haut de gamme spécialisée dans les propriétés commerciales et résidentielles.",
      descriptionEs: "Firma premium de desarrollo y gestión inmobiliaria especializada en propiedades comerciales y residenciales.",
      descriptionAr: "شركة متميزة لتطوير وإدارة العقارات متخصصة في العقارات التجارية والسكنية.",
      link: "https://www.instagram.com/marvelimmobilien/?hl=en"
    },
    {
      id: 6,
      name: "BSS Group Holding",
      category: "Conglomerate",
      categoryTr: "Holding",
      categoryFr: "Conglomérat",
      categoryEs: "Conglomerado",
      categoryAr: "تكتل",
      image: bssGroupImg,
      description: "Multinational holding company with interests in finance, technology, real estate, and business services.",
      descriptionTr: "Finans, teknoloji, gayrimenkul ve iş hizmetleri alanlarında faaliyet gösteren çok uluslu holding şirketi.",
      descriptionFr: "Société holding multinationale avec des intérêts dans la finance, la technologie, l'immobilier et les services aux entreprises.",
      descriptionEs: "Empresa holding multinacional con intereses en finanzas, tecnología, bienes raíces y servicios empresariales.",
      descriptionAr: "شركة قابضة متعددة الجنسيات لها مصالح في التمويل والتكنولوجيا والعقارات وخدمات الأعمال.",
      link: "https://www.instagram.com/bssholding/"
    },
    {
      id: 7,
      name: "World Business AG",
      category: "Business Services",
      categoryTr: "İş Hizmetleri",
      categoryFr: "Services aux Entreprises",
      categoryEs: "Servicios Empresariales",
      categoryAr: "خدمات الأعمال",
      image: worldBusinessImg,
      description: "Global business services provider delivering comprehensive solutions for international commerce and corporate operations.",
      descriptionTr: "Uluslararası ticaret ve kurumsal operasyonlar için kapsamlı çözümler sunan küresel iş hizmetleri sağlayıcısı.",
      descriptionFr: "Fournisseur mondial de services aux entreprises offrant des solutions complètes pour le commerce international et les opérations d'entreprise.",
      descriptionEs: "Proveedor global de servicios empresariales que ofrece soluciones integrales para el comercio internacional y las operaciones corporativas.",
      descriptionAr: "مزود عالمي لخدمات الأعمال يقدم حلولاً شاملة للتجارة الدولية والعمليات المؤسسية.",
      link: "https://www.worldbusinessag.com/"
    },
    {
      id: 8,
      name: "Gluckspilz Gastro",
      category: "Hospitality",
      categoryTr: "Konaklama",
      categoryFr: "Hôtellerie",
      categoryEs: "Hostelería",
      categoryAr: "الضيافة",
      image: gluckspilzImg,
      description: "Premium hospitality group managing innovative restaurant concepts and gastronomic experiences across various locations throughout Europe.",
      descriptionTr: "Avrupa genelinde çeşitli lokasyonlarda yenilikçi restoran konseptleri ve gastronomik deneyimler sunan premium konaklama grubu.",
      descriptionFr: "Groupe hôtelier haut de gamme gérant des concepts de restaurants innovants et des expériences gastronomiques dans différents endroits à travers l'Europe.",
      descriptionEs: "Grupo hotelero premium que gestiona conceptos innovadores de restaurantes y experiencias gastronómicas en varias ubicaciones por toda Europa.",
      descriptionAr: "مجموعة ضيافة فاخرة تدير مفاهيم مطاعم مبتكرة وتجارب طهي في مواقع متنوعة في جميع أنحاء أوروبا.",
      link: "https://deineportionglueck.de/"
    }
  ], []);

  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const companyParam = queryParams.get('company');

  // Set active company based on URL parameter on component mount
  useEffect(() => {
    if (companyParam) {
      let matchingIndex = -1;
      
      // Handle specific company mappings
      if (companyParam === 'yes-group') {
        matchingIndex = companies.findIndex(c => c.name === 'YES Group Holding');
      } else if (companyParam === 'all-software') {
        matchingIndex = companies.findIndex(c => c.name === 'AllSoftware24 L.L.C-FZ');
      } else if (companyParam === 'gluckspilz') {
        matchingIndex = companies.findIndex(c => c.name === 'Gluckspilz Gastro');
      } else if (companyParam === 'world-business-ag') {
        matchingIndex = companies.findIndex(c => c.name === 'World Business AG');
      } else if (companyParam === 'netflipp') {
        matchingIndex = companies.findIndex(c => c.name === 'Netflipp');
      } else if (companyParam === 'bss-holding') {
        matchingIndex = companies.findIndex(c => c.name === 'BSS Holding');
      } else {
        // Fallback to general matching
        const companyFromParam = companies.find(
          company => company.name.toLowerCase().replace(/\s+/g, '-') === companyParam
        );
        
        if (companyFromParam) {
          matchingIndex = companies.findIndex(c => c.id === companyFromParam.id);
        }
      }
      
      // Set active index but don't open modal
      if (matchingIndex !== -1) {
        setActiveIndex(matchingIndex);
        // Modalı otomatik olarak açmayı kaldırdık
      }
    }
  }, [companyParam, companies]);
  
  // Number of cards visible at once (adjust based on screen size)
  const getVisibleCards = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth <= 480) {
        return 1; // Only 1 card visible on mobile
      } else if (window.innerWidth <= 768) {
        return 2; // 2 cards visible on tablet
      }
    }
    return 3; // 3 cards visible on desktop
  };
  
  // Calculate adjustments for responsive design
  const getCardWidth = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth <= 480) {
        return 260 + 20; // Card width + margins for mobile
      } else if (window.innerWidth <= 768) {
        return 280 + 30; // Card width + margins for tablet
      }
    }
    return 320 + 40; // Card width + margins for desktop
  };
  
  // Recalculate on window resize and component mount
  useEffect(() => {
    const handleResize = () => {
      setTrackWidth(getCardWidth());
      setVisibleCards(getVisibleCards());
    };
    
    // Handle initial load
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Improve touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
  }, []);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const swipeDistance = touchEndXRef.current! - touchStartXRef.current!;
    
    // Only process significant swipes to avoid accidental triggers
    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0) {
        // Swiped right - go to previous
        if (activeIndex > 0) {
          setActiveIndex(activeIndex - 1);
        }
      } else {
        // Swiped left - go to next
        if (activeIndex < companies.length - visibleCards) {
          setActiveIndex(activeIndex + 1);
        }
      }
    }
  }, [activeIndex, companies.length, visibleCards]);
  
  // Handle navigation
  const handleNext = (e?: React.MouseEvent | React.TouchEvent) => {
    // Always stop propagation and prevent default
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (activeIndex < companies.length - visibleCards) {
      // Move only one item at a time
      setActiveIndex(prevIndex => prevIndex + 1);
    }
  };
  
  const handlePrev = (e?: React.MouseEvent | React.TouchEvent) => {
    // Always stop propagation and prevent default
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (activeIndex > 0) {
      // Move only one item at a time
      setActiveIndex(prevIndex => prevIndex - 1);
    }
  };
  
  // Handle card click
  const handleCardClick = (company: Company) => {
    // Aktif şirket değişecek
    setActiveIndex(companies.findIndex(c => c.id === company.id));
    
    // Detay bölümüne scroll yap
    setTimeout(() => {
      if (companyDetailRef.current) {
        companyDetailRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Open modal - sadece View Details butonu için kullanılacak
  const openCompanyModal = (company: Company) => {
    setSelectedCompany(company);
  };
  
  // Close modal
  const closeCompanyModal = () => {
    setSelectedCompany(null);
    
    // If we have a company parameter in URL, remove it when closing modal
    if (companyParam) {
      navigate('/companies', { replace: true });
    }
  };
  
  // Translations object for Companies page
  const translations = {
    en: {
      pageTitle: "Featured Companies",
      pageSubtitle: "Discover innovative companies pushing the boundaries of technology and changing the world with their groundbreaking solutions.",
      featuredProjectTitle: "Featured Project: Trade on tv",
      tradingReimaginedTitle: "Trading Reimagined for the Big Screen",
      tradingReimaginedDescription: "Trade on tv is a revolutionary platform that brings the power of financial markets directly to your television. With an intuitive interface optimized for TV screens, it transforms how investors interact with markets from the comfort of their living rooms.",
      visitTradeOnTv: "Visit Trade on tv",
      viewCompanyWebsite: "Visit Website",
      viewSocialMedia: "Visit Social"
    },
    tr: {
      pageTitle: "Öne Çıkan Şirketler",
      pageSubtitle: "Teknolojinin sınırlarını zorlayan ve çığır açan çözümleriyle dünyayı değiştiren yenilikçi şirketleri keşfedin.",
      featuredProjectTitle: "Öne Çıkan Proje: Trade on tv",
      tradingReimaginedTitle: "Büyük Ekran için Yeniden Tasarlanan Trade",
      tradingReimaginedDescription: "Trade on tv, finansal piyasaların gücünü doğrudan televizyonunuza getiren devrim niteliğinde bir platformdur. TV ekranları için optimize edilmiş sezgisel arayüzü ile yatırımcıların piyasalarla oturma odalarının konforundan nasıl etkileşime girdiğini dönüştürür.",
      visitTradeOnTv: "Trade on tv'yi Ziyaret Et",
      viewCompanyWebsite: "Web Sitesini Ziyaret Et",
      viewSocialMedia: "Sosyal Medyayı Ziyaret Et"
    },
    fr: {
      pageTitle: "Entreprises en Vedette",
      pageSubtitle: "Découvrez des entreprises innovantes qui repoussent les limites de la technologie et changent le monde avec leurs solutions révolutionnaires.",
      featuredProjectTitle: "Projet en Vedette: Trade on tv",
      tradingReimaginedTitle: "Le Trading Réimaginé pour le Grand Écran",
      tradingReimaginedDescription: "Trade on tv est une plateforme révolutionnaire qui apporte la puissance des marchés financiers directement sur votre télévision. Avec une interface intuitive optimisée pour les écrans TV, elle transforme la façon dont les investisseurs interagissent avec les marchés depuis le confort de leur salon.",
      visitTradeOnTv: "Visiter Trade on tv",
      viewCompanyWebsite: "Visiter le Site Web",
      viewSocialMedia: "Visiter les Réseaux Sociaux"
    },
    es: {
      pageTitle: "Empresas Destacadas",
      pageSubtitle: "Descubra empresas innovadoras que están empujando los límites de la tecnología y cambiando el mundo con sus soluciones revolucionarias.",
      featuredProjectTitle: "Proyecto Destacado: Trade on tv",
      tradingReimaginedTitle: "Trading Reimaginado para la Pantalla Grande",
      tradingReimaginedDescription: "Trade on tv es una plataforma revolucionaria que lleva el poder de los mercados financieros directamente a su televisor. Con una interfaz intuitiva optimizada para pantallas de TV, transforma la forma en que los inversores interactúan con los mercados desde la comodidad de sus salas de estar.",
      visitTradeOnTv: "Visitar Trade on tv",
      viewCompanyWebsite: "Visitar Sitio Web",
      viewSocialMedia: "Visitar Redes Sociales"
    },
    ar: {
      pageTitle: "الشركات المميزة",
      pageSubtitle: "اكتشف الشركات المبتكرة التي تدفع حدود التكنولوجيا وتغير العالم بحلولها الرائدة.",
      featuredProjectTitle: "المشروع المميز: Trade on tv",
      tradingReimaginedTitle: "إعادة تصور التداول للشاشة الكبيرة",
      tradingReimaginedDescription: "Trade on tv هي منصة ثورية تجلب قوة الأسواق المالية مباشرة إلى تلفزيونك. مع واجهة بديهية محسنة لشاشات التلفزيون، تحول كيفية تفاعل المستثمرين مع الأسواق من راحة غرف معيشتهم.",
      visitTradeOnTv: "زيارة Trade on tv",
      viewCompanyWebsite: "زيارة الموقع الإلكتروني",
      viewSocialMedia: "زيارة وسائل التواصل الاجتماعي"
    }
  };
  
  // Get text based on the current language
  const getText = (key: keyof typeof translations.en): string => {
    if (language === 'en' || language === 'tr' || language === 'fr' || language === 'es' || language === 'ar') {
      return translations[language as keyof typeof translations][key];
    }
    return translations.en[key];
  };
  
  // Derive the X position for the carousel track
  const trackX = -(activeIndex * trackWidth); // Use responsive width

  // Handle BSS Holding image differently to account for photograph vs logo
  const getImageStyle = (company: Company) => {
    if (company.name === "BSS Holding") {
      return { objectFit: 'cover' as const };
    }
    return { objectFit: 'contain' as const };
  };

  return (
    <CompaniesSection>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        schema={companySchema}
      />
      
      {/* This section has overflow-x: hidden to prevent unwanted horizontal scrolling */}
      <CompaniesContainer>
        <CompaniesHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {getText('pageTitle')}
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {getText('pageSubtitle')}
          </PageSubtitle>
        </CompaniesHeader>
        
        {/* Active Company Showcase */}
        <AnimatePresence mode="wait">
          <ActiveCompanyContainer
            ref={companyDetailRef}
            as={motion.div}
            key={companies[activeIndex]?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ActiveCompanyImageContainer>
              <ActiveCompanyImage src={companies[activeIndex]?.image} alt={companies[activeIndex]?.name} />
            </ActiveCompanyImageContainer>
            <ActiveCompanyContent>
              <ActiveCompanyCategory>
                {language === 'tr' && companies[activeIndex]?.categoryTr ? companies[activeIndex]?.categoryTr :
                 language === 'fr' && companies[activeIndex]?.categoryFr ? companies[activeIndex]?.categoryFr :
                 language === 'es' && companies[activeIndex]?.categoryEs ? companies[activeIndex]?.categoryEs :
                 language === 'ar' && companies[activeIndex]?.categoryAr ? companies[activeIndex]?.categoryAr :
                 companies[activeIndex]?.category}
              </ActiveCompanyCategory>
              <ActiveCompanyName>{companies[activeIndex]?.name}</ActiveCompanyName>
              <ActiveCompanyDescription>
                {language === 'tr' && companies[activeIndex]?.descriptionTr ? companies[activeIndex]?.descriptionTr :
                 language === 'fr' && companies[activeIndex]?.descriptionFr ? companies[activeIndex]?.descriptionFr :
                 language === 'es' && companies[activeIndex]?.descriptionEs ? companies[activeIndex]?.descriptionEs :
                 language === 'ar' && companies[activeIndex]?.descriptionAr ? companies[activeIndex]?.descriptionAr :
                 companies[activeIndex]?.description}
              </ActiveCompanyDescription>
              <ActiveCompanyLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  openCompanyModal(companies[activeIndex]);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {language === 'tr' ? 'Detayları Görüntüle' :
                 language === 'fr' ? 'Voir les Détails' :
                 language === 'es' ? 'Ver Detalles' :
                 language === 'ar' ? 'عرض التفاصيل' :
                 'View Details'}
              </ActiveCompanyLink>
            </ActiveCompanyContent>
          </ActiveCompanyContainer>
        </AnimatePresence>
        
        {/* Carousel */}
        <CarouselContainer>
          {activeIndex > 0 && (
            <SwipeIndicator className="left">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 20 8 12 16 4"></polyline>
              </svg>
            </SwipeIndicator>
          )}

          {activeIndex < companies.length - visibleCards && (
            <SwipeIndicator className="right">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="8 4 16 12 8 20"></polyline>
              </svg>
            </SwipeIndicator>
          )}

          <PrevButton 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePrev(e);
            }}
            disabled={activeIndex === 0}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Previous slide"
            role="button"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 20 8 12 16 4"></polyline>
            </svg>
          </PrevButton>
          
          <NextButton 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleNext(e);
            }}
            disabled={activeIndex >= companies.length - visibleCards}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Next slide"
            role="button"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="8 4 16 12 8 20"></polyline>
            </svg>
          </NextButton>
          
          <CarouselTrack
            animate={{ x: trackX }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {companies.map((company, index) => (
              <CarouselCard
                key={company.id}
                onClick={() => handleCardClick(company)}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileTap={{ scale: 0.98 }}
                style={{ 
                  opacity: activeIndex === index ? 1 : 0.7,
                  scale: activeIndex === index ? 1.05 : 1,
                  boxShadow: activeIndex === index ? '0 15px 30px rgba(0, 0, 0, 0.15)' : '0 10px 20px rgba(0, 0, 0, 0.05)',
                  border: activeIndex === index ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.05)'
                }}
              >
                {company.name === "World Business AG" ? (
                  <WorldBusinessImageWrapper>
                    <WorldBusinessCompanyImage src={company.image} alt={company.name} />
                  </WorldBusinessImageWrapper>
                ) : (
                  <CompanyImageWrapper>
                    <CompanyImage 
                      src={company.image} 
                      alt={company.name} 
                      style={getImageStyle(company)}
                    />
                  </CompanyImageWrapper>
                )}
                <CompanyContent>
                  <CompanyCategory>
                    {language === 'tr' && company.categoryTr ? company.categoryTr :
                     language === 'fr' && company.categoryFr ? company.categoryFr :
                     language === 'es' && company.categoryEs ? company.categoryEs :
                     language === 'ar' && company.categoryAr ? company.categoryAr :
                     company.category}
                  </CompanyCategory>
                  <CompanyName>{company.name}</CompanyName>
                  <CompanyDescription>
                    {language === 'tr' && company.descriptionTr ? company.descriptionTr :
                     language === 'fr' && company.descriptionFr ? company.descriptionFr :
                     language === 'es' && company.descriptionEs ? company.descriptionEs :
                     language === 'ar' && company.descriptionAr ? company.descriptionAr :
                     company.description}
                  </CompanyDescription>
                </CompanyContent>
              </CarouselCard>
            ))}
          </CarouselTrack>
          
          <CarouselDots>
            {companies.slice(0, companies.length - visibleCards + 1).map((_, index) => (
              <Dot 
                key={index} 
                active={index === activeIndex}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
                type="button"
              />
            ))}
          </CarouselDots>
        </CarouselContainer>
        
        <FeaturedSection>
          <FeaturedTitle
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {getText('featuredProjectTitle')}
          </FeaturedTitle>
          <FeaturedContent>
            <FeaturedDescription>
              <h3>{getText('tradingReimaginedTitle')}</h3>
              <p>
                {getText('tradingReimaginedDescription')}
              </p>
              <ul>
                <li>
                  {language === 'tr' ? 'Küresel borsalarda gerçek zamanlı piyasa verileri' :
                   language === 'fr' ? 'Données de marché en temps réel sur les bourses mondiales' :
                   language === 'es' ? 'Datos de mercado en tiempo real en bolsas globales' :
                   language === 'ar' ? 'بيانات السوق في الوقت الحقيقي عبر البورصات العالمية' :
                   'Real-time market data across global exchanges'}
                </li>
                <li>
                  {language === 'tr' ? 'Görsel analizlerle akıllı portföy yönetim araçları' :
                   language === 'fr' ? 'Outils de gestion de portefeuille intelligents avec analyses visuelles' :
                   language === 'es' ? 'Herramientas inteligentes de gestión de cartera con análisis visuales' :
                   language === 'ar' ? 'أدوات ذكية لإدارة المحافظ مع تحليلات مرئية' :
                   'Smart portfolio management tools with visual analytics'}
                </li>
                <li>
                  {language === 'tr' ? 'Büyük yayın cihazlarıyla sorunsuz entegrasyon' :
                   language === 'fr' ? 'Intégration transparente avec les principaux appareils de streaming' :
                   language === 'es' ? 'Integración perfecta con los principales dispositivos de streaming' :
                   language === 'ar' ? 'تكامل سلس مع أجهزة البث الرئيسية' :
                   'Seamless integration with major streaming devices'}
                </li>
                <li>
                  {language === 'tr' ? 'Sesle kontrol edilen ticaret ve piyasa araştırması' :
                   language === 'fr' ? 'Trading et étude de marché contrôlés par la voix' :
                   language === 'es' ? 'Comercio e investigación de mercado controlados por voz' :
                   language === 'ar' ? 'التداول وأبحاث السوق التي تتم التحكم فيها بالصوت' :
                   'Voice-controlled trading and market research'}
                </li>
                <li>
                  {language === 'tr' ? 'Özelleştirilebilir uyarılar ve bildirimler' :
                   language === 'fr' ? 'Alertes et notifications personnalisables' :
                   language === 'es' ? 'Alertas y notificaciones personalizables' :
                   language === 'ar' ? 'تنبيهات وإشعارات قابلة للتخصيص' :
                   'Customizable alerts and notifications'}
                </li>
              </ul>
              <FeaturedLink 
                href="https://tradeontv.com" 
                target="_blank"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {getText('visitTradeOnTv')}
              </FeaturedLink>
            </FeaturedDescription>
            <FeaturedImage>
              <img src={tradeOnTvImg} alt="Trade on tv Platform" />
            </FeaturedImage>
          </FeaturedContent>
        </FeaturedSection>
      </CompaniesContainer>
      
      {/* Eski referansı kaldırdık */}

      {selectedCompany && (
        <Modal onClose={closeCompanyModal}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <ModalCompanyImage>
              <img 
                src={selectedCompany.image} 
                alt={selectedCompany.name} 
                style={getImageStyle(selectedCompany)}
              />
              </ModalCompanyImage>
            <ModalCompanyName>{selectedCompany.name}</ModalCompanyName>
            <ModalCompanyCategory>
              {language === 'tr' && selectedCompany.categoryTr ? selectedCompany.categoryTr :
               language === 'fr' && selectedCompany.categoryFr ? selectedCompany.categoryFr :
               language === 'es' && selectedCompany.categoryEs ? selectedCompany.categoryEs :
               language === 'ar' && selectedCompany.categoryAr ? selectedCompany.categoryAr :
               selectedCompany.category}
            </ModalCompanyCategory>
            <ModalCompanyDescription>
              {language === 'tr' && selectedCompany.descriptionTr ? selectedCompany.descriptionTr :
               language === 'fr' && selectedCompany.descriptionFr ? selectedCompany.descriptionFr :
               language === 'es' && selectedCompany.descriptionEs ? selectedCompany.descriptionEs :
               language === 'ar' && selectedCompany.descriptionAr ? selectedCompany.descriptionAr :
               selectedCompany.description}
            </ModalCompanyDescription>
            
            {selectedCompany.link && (
              <ModalCompanyLink href={selectedCompany.link} target="_blank" rel="noopener noreferrer">
                {selectedCompany.link.includes('instagram') ? getText('viewSocialMedia') : getText('viewCompanyWebsite')}
              </ModalCompanyLink>
            )}
          </div>
        </Modal>
      )}
    </CompaniesSection>
  );
} 